import { historyService } from './historyService';
import { supabase } from '../lib/supabase';

export interface MemorySummary {
  hasPreviousExploration: boolean;
  summary: string;
  unexploredAngles: string[];
  explorationCount: number;
}

export class MemoryLLMService {
  async generateMemorySummary(query: string): Promise<MemorySummary | null> {
    try {
      console.log('Generating memory summary for query:', query);
      
      // Get more history to filter from (up to 30 items)
      const allHistory = await historyService.getUserHistory(30);
      console.log('Total history items available:', allHistory.length);
      
      if (allHistory.length === 0) {
        return this.getDefaultSummary();
      }
      
      // First, filter the history for relevant items
      console.log('Filtering history for relevant explorations...');
      const { data: filterResult, error: filterError } = await supabase.functions.invoke('memory-filter', {
        body: {
          query,
          history: allHistory
        }
      });
      
      if (filterError) {
        console.error('Error calling memory filter:', filterError);
        // Fallback to using recent history if filter fails
        const fallbackHistory = allHistory.slice(0, 10);
        return this.generateSummaryFromHistory(query, fallbackHistory);
      }
      
      // Get the relevant explorations based on filter results
      const relevantIds = filterResult?.relevantIds || [];
      console.log(`Filter identified ${relevantIds.length} relevant explorations:`, relevantIds);
      
      let relevantHistory = [];
      if (relevantIds.length > 0) {
        relevantHistory = allHistory.filter(item => relevantIds.includes(item.id));
        console.log(`Using ${relevantHistory.length} filtered explorations for memory summary`);
        relevantHistory.forEach(item => {
          console.log(`  - "${item.data?.query || 'No query'}" (ID: ${item.id})`);
        });
      } else {
        // If no relevant history found, use most recent as fallback
        relevantHistory = allHistory.slice(0, 3);
        console.log('No relevant history found by filter, using 3 most recent explorations as fallback');
        relevantHistory.forEach(item => {
          console.log(`  - Fallback: "${item.data?.query || 'No query'}" (ID: ${item.id})`);
        });
      }
      
      // Also get user's memory data for additional context
      const { data: userData } = await supabase
        .from('user_data')
        .select('memory')
        .eq('user_id', (await supabase.auth.getUser()).data.user?.id)
        .maybeSingle();
      
      const memory = userData?.memory;
      console.log('User memory data exists:', !!memory);
      
      // Generate summary using filtered history
      return this.generateSummaryFromHistory(query, relevantHistory);
    } catch (error) {
      console.error('Failed to generate memory summary:', error);
      return this.getDefaultSummary();
    }
  }
  
  private async generateSummaryFromHistory(query: string, history: any[]): Promise<MemorySummary | null> {
    if (history.length === 0) {
      return this.getDefaultSummary();
    }
    
    console.log(`Calling memory-llm with ${history.length} relevant explorations`);
    const { data, error } = await supabase.functions.invoke('memory-llm', {
      body: {
        query,
        relatedHistory: history
      }
    });

    if (error) {
      console.error('Error calling memory LLM edge function:', error);
      return this.getDefaultSummary();
    }

    console.log('Memory LLM response:', data);
    return data as MemorySummary;
  }

  private getDefaultSummary(): MemorySummary {
    return {
      hasPreviousExploration: false,
      summary: "Welcome to Lore Spirals! As you explore different fandoms and topics, we'll remember your journey. This space will show what you've discovered before, helping you build on previous explorations and spiral deeper into the lore.",
      unexploredAngles: [],
      explorationCount: 0
    };
  }
}

export const memoryLLMService = new MemoryLLMService();